<template>
    <div :class="{fullscreen: isFullscreen, workflow: true}">
        <b-row class="p-2">
            <b-col cols="3"> 
                <b-icon class="mr-3" icon="plus-circle-fill" @click="handleDblClick"/>

                <b-icon class="mr-3" icon="pencil" @click="handleEditNode(selectedNode)" v-if="selectedNode" />
                <b-icon class="mr-3" icon="trash" @click="handleRemoveNode(selectedNode)" v-if="selectedNode" />

                <b-icon :id="`controls-popover`" icon="toggles" />
                <b-popover :target="`controls-popover`"
                            placement="right"
                            title="Easy to use"
                            triggers="hover focus"
                            variant="dark"
                            >
                    <template slot="title"> {{ $t('easyToUse') }}</template>
                    <code> {{ $t('canvasDbl') }}</code> <br />
                    <code> {{ $t('nodeDbl') }}</code> <br />
                    <code> {{ $t('removeNode') }}</code> <br />
                </b-popover>
            </b-col>
            <b-col offset="8" cols="1" align-self="end"> <span style="cursor:pointer" @click="isFullscreen = !isFullscreen"> <b-icon icon="fullscreen" v-if="!isFullscreen"/> <b-icon icon="fullscreen-exit" v-else/></span> </b-col>
        </b-row>
       
        <b-form-group v-if="nodes && connections">
            <FlowChart
                :nodes="nodes"
                :connections="connections"
                @editnode="handleEditNode"
                @dblclick="handleDblClick"
                @editconnection="handleEditConnection"
                @save="handleChartSave"
                @dragend="dragend"
                :createConnection="createConnection"
                ref="chart"
                :width="'100%'"
                :render="render"
                :key="version"
                >
            </FlowChart>
        </b-form-group>

        <b-modal title="Add node" v-model="newNode.modal" ok-title="Add" @ok="newNodeAdd">
            <SelectField name="type" :label="this.$t('type')" v-model="newNode.data.type" @error="fieldError"
                :items="['event', 'operation', 'condition', 'each', 'switch']"
                :reduce="(o) => o"
                :rules="{required: true}"
            />

            <div v-if="newNode.data.type == 'event'">
                <v-select name="name" label="label" v-model="newNodeName" @error="fieldError"
                    :options="eventNamesFormated"
                    taggable
                    :reduce="eventNameReducer"
                />
            </div>

            <div v-if="newNode.data.type == 'event'">
                <div v-if="newNode.data.name == 'Recycle'">
                    <SelectField name="timer" :label="this.$t('timer')" v-model="newNode.data.data.timer" @error="fieldError"
                        :items="timers"
                        :rules="{required: true}"
                        v-if="newNode.data.name == 'Recycle'"
                        :description="this.$t('eventTriggered')"
                    />
                    <Helptip helpType="icon" tipId="event.recycle.ruleset" size="sm">
                        <CodeEditor
                            v-model="newNode.data.data.expression"
                            :label="this.$t('ruleset')"
                            :label-cols="2"
                            :horizontal="true"
                            :controls='[""]'
                            :vars="[{name: 'ruleset', usage: `{}`}]"
                            :libs="rulesetLibs"
                            :errors="recycleExprErrors"
                        />
                    </Helptip>
                </div>

                <b-form-group
                    :label="this.$t('paused')"
                    label-for="module"
                    :label-cols="2"
                    label-cols-sm="2"
                    class="mt-4"
                    :horizontal="false">
                        <c-switch class="mx-1" color="success" variant="pill" :label="true" :dataOn="$t('yes')" :dataOff="$t('no')" v-model="newNode.data.paused"/>
                </b-form-group>
            </div>



            <template v-if="newNode.data.type == 'operation'">
                <InputField name="name" :label="this.$t('name')" v-model="newNodeName" @error="fieldError"
                    :rules="{required:true, length: {min: 2}}"
                />

                <SelectField name="action" :label="this.$t('action')" v-model="newNode.data.action" @error="fieldError"
                    :items="actions"
                    :rules="{required: true}"
                />
            </template>

             <template v-if="newNode.data.type == 'condition'">
                <InputField name="name" :label="this.$t('name')" v-model="newNodeName" @error="fieldError"
                    :rules="{required:true, length: {min: 2}}"
                />


                <b-form-group
                    :label-cols="0"
                    :horizontal="false"
                >

                    <ConditionExpression v-model="newNode.data.data" />
                </b-form-group>

            </template>

            <template v-if="newNode.data.type == 'switch'">
                <InputField name="name" :label="this.$t('name')" v-model="newNodeName" @error="fieldError"
                    :rules="{required:true, length: {min: 2}}"
                />


                <b-form-group
                    :label-cols="0"
                    :horizontal="false"
                >

                    <SwitchExpression v-model="newNode.data.data" />
                </b-form-group>

            </template>

            <b-form-group
                :label="this.$t('email')"
                :label-cols="2"
                :horizontal="false"
                v-if="newNode.data.action == 'email' && newNode.data.type == 'operation'"
            >
                <Email v-model="newNode.data.data" />
            </b-form-group>

            <b-form-group
                :label="this.$t('notification')"
                :label-cols="2"
                :horizontal="false"
                v-if="newNode.data.action == 'platform_notification' && newNode.data.type == 'operation'"
            >
                <PlatformNotifications v-model="newNode.data.data" />
            </b-form-group>

            <b-form-group
                :label="this.$t('notification')"
                :label-cols="2"
                :horizontal="false"
                v-if="newNode.data.action == 'push_notification'  && newNode.data.type == 'operation'"
            >
                <PushNotification v-model="newNode.data.data" />
            </b-form-group>

            <b-form-group
                :label="this.$t('request')"
                :label-cols="2"
                :horizontal="false"
                v-if="newNode.data.action == 'http_request'  && newNode.data.type == 'operation'"
            >
                <HttpRequest v-model="newNode.data.data" />
            </b-form-group>


            <b-form-group
                :label-cols="0"
                :horizontal="false"
                v-if="newNode.data.action == 'expression'  && newNode.data.type == 'operation'"
            >

                <Expression v-model="newNode.data.data" />
            </b-form-group>

            <b-form-group
                :label-cols="0"
                :horizontal="false"
                v-if="newNode.data.action == 'component'  && newNode.data.type == 'operation'"
            >
                <ComponentExpression v-model="newNode.data.data" />
            </b-form-group>

            <template v-if="newNode.data.action == 'spark' && newNode.data.type == 'operation'">
                <Spark v-model="newNode.data.data" />
            </template>

        </b-modal>

        <b-modal title="Edit node" v-model="editNode.modal" hide-footer ref="editNodeModal" @hide="closeNodeEdit">
            <v-select name="name" label="label" v-model="editNodeName" @error="fieldError"
                :options="eventNamesFormated"
                v-if="editNode.data.type == 'event'"
                taggable
                :reduce="o => o.name"
            />

            <div v-if="editNode.data.type == 'event'">
              <div v-if="editNode.data.name == 'Recycle'">
                    <SelectField name="timer" :label="this.$t('timer')" v-model="editNode.data.data.timer" @error="fieldError"
                        :items="timers"
                        :rules="{required: true}"
                        v-if="editNode.data.name == 'Recycle'"
                        :description="this.$t('eventTriggeredInTime')"
                    />
                    <Helptip helpType="icon" tipId="event.recycle.ruleset" size="sm">
                        <CodeEditor
                            v-model="editNode.data.data.expression"
                            :label="this.$t('ruleset')"
                            :label-cols="2"
                            :horizontal="true"
                            :controls='[""]'
                            :vars="[{name: 'ruleset', usage: `{}`}]"
                            :libs="rulesetLibs"
                            :errors="recycleExprErrors"
                        />
                    </Helptip>
                </div>

                 <b-form-group
                    :label="this.$t('paused')"
                    label-for="module"
                    :label-cols="2"
                    label-cols-sm="2"
                    class="mt-4"
                    :horizontal="false">
                    <c-switch class="mx-1" color="success" variant="pill" :label="true" dataOn="Yes" dataOff="No" v-model="editNode.data.paused"/>
                </b-form-group>
            </div>

            <template v-if="editNode.data.type == 'condition'">
                <InputField name="name" :label="this.$t('name')" v-model="editNodeName" @error="fieldError"
                    :rules="{required:true, length: {min: 2}}"
                />


                <b-form-group
                    :label-cols="0"
                    :horizontal="false"
                >
                    <ConditionExpression v-model="editNode.data.data" />
                </b-form-group>

            </template>

            <template v-if="editNode.data.type == 'switch'">
                <InputField name="name" :label="this.$t('name')" v-model="editNodeName" @error="fieldError"
                    :rules="{required:true, length: {min: 2}}"
                />


                <b-form-group
                    :label-cols="0"
                    :horizontal="false"
                >
                    <SwitchExpression v-model="editNode.data.data" />
                </b-form-group>

            </template>

            <template v-if="editNode.data.type == 'operation'">
                <InputField name="name" :label="this.$t('name')" v-model="editNodeName" @error="fieldError"
                    :rules="{required:true, length: {min: 2}}"
                />

                <b-form-group
                    :label="this.$t('email')"
                    :label-cols="2"
                    :horizontal="false"
                    v-if="editNode.data.action == 'email'"
                >
                    <Email v-model="editNode.data.data" />
                </b-form-group>

                 <b-form-group
                label="Notification"
                :label-cols="2"
                :horizontal="false"
                v-if="editNode.data.action == 'platform_notification'"
            >
                <PlatformNotifications v-model="editNode.data.data" />
            </b-form-group>

                <b-form-group
                    :label="this.$t('notification')"
                    :label-cols="2"
                    :horizontal="false"
                    v-if="editNode.data.action == 'push_notification'"
                >
                    <PushNotification v-model="editNode.data.data" />
                </b-form-group>

                <b-form-group
                    :label="this.$t('request')"
                    :label-cols="2"
                    :horizontal="false"
                    v-if="editNode.data.action == 'http_request'"
                >
                    <HttpRequest v-model="editNode.data.data" />

                </b-form-group>

                <b-form-group
                    :label-cols="0"
                    :horizontal="false"
                    v-if="editNode.data.action == 'expression'"
                >

                    <Expression v-model="editNode.data.data" />
                </b-form-group>

                <b-form-group
                    :label-cols="0"
                    :horizontal="false"
                    v-if="editNode.data.action == 'component'"
                >
                    <ComponentExpression v-model="editNode.data.data" />
                </b-form-group>

                <template v-if="editNode.data.action == 'spark'">
                <Spark v-model="editNode.data.data" />
            </template>
            </template>
        </b-modal>

        <b-modal v-model="editConnectionModal" hide-footer>
            <InputField name="path" :label="this.$t('path')" v-model="editConnectionName" @error="fieldError"
                :rules="{required:true, length: {min: 2}}"
            />
        </b-modal>

        <Drawer @close="toggleResultsSidebar" align="down" :mask="false" :z-index="9999">
            <div class="p-2" v-if="inspectResult && resultsSidebar">
                <tree-view :data="inspectResult.State.Results" :options="{maxDepth: 2, rootObjectKey:'results'}"></tree-view>
                <tree-view :data="inspectResult.data" :options="{maxDepth: 2, rootObjectKey:'data'}"></tree-view>
            </div>
        </Drawer>
        

        <b-sidebar v-model="dryRunInputModal" right backdrop width="500px">
            <template v-if="isDryRunCustomEvent">
                <div class="p-2">
                    <CodeEditor
                        v-model="dryRunInput"
                        :controls='[""]'
                        placeholder="{}"
                        @onInput="onDryInput"
                        :errors="dryRunInputErrors"
                        :withButton="false"
                    />
                </div>
            </template>

            <template v-else>
                <CreateModel :id="uuid" @modelChanged="modelChanged" entrypoint="default" :settings="{container: {cols:12,md:12}}">
                    <template #controls>
                        <span></span>
                    </template>
                </CreateModel>
            </template>

            <template #footer>
                <div class="d-flex bg-dark text-light align-items-center px-3 py-2">
                    <b-button variant="success" @click="dryRun()"><b-icon icon="play" /></b-button>
                </div>
            </template>
        </b-sidebar>
    </div>
</template>

<style scoped>
.flowchart {
    width: 100% !important;
}

.event-modal .modal-dialog {
    max-width: 100%;
    margin: 0;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100vh;
    display: flex;
    position: fixed;
    z-index: 100000;
}

.condition {
    width: 200px;
    height: 200px;
}

.dryRunResults {
    position: fixed;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    z-index: 9999;
    background-color: rgba(0, 0, 0, 0.7)
}

.workflow {
    background-color: #373D41;
}
</style>

<script>
import Input from '@/components/form/Input'
import Select from "@/components/form/Select"
import CodeEditor from '@/components/form/Editor/Code'
import * as d3 from 'd3'
import _ from 'lodash'
import { Switch as cSwitch } from '@coreui/vue'
import Drawer from "vue-simple-drawer"

import Email from './Events/Email'
import PlatformNotifications from './Events/PlatformNotifications'
import PushNotification from './Events/PushNotification'
import HttpRequest from './Events/HttpRequest'
import Expression from './Events/Expression'
import ConditionExpression from './Events/ConditionExpression'
import ComponentExpression from './Events/ComponentExpression'
import SwitchExpression from './Events/SwitchExpression'
import Spark from './Events/Spark'
import FlowChart from '../../flowchart-vue/src/components/flowchart';
import Helptip from '../../elements/Helptip'
import VSelect from 'vue-select'
import CreateModel from '@/components/base/Create'

export default {
    components: {
        InputField: Input,
        SelectField: Select,
        Email,
        PlatformNotifications,
        PushNotification,
        HttpRequest,
        Expression,
        ConditionExpression,
        ComponentExpression,
        SwitchExpression,
        FlowChart,
        CodeEditor,
        Helptip,
        VSelect,
        cSwitch,
        CreateModel,
        Drawer,
        Spark
    },
    props: {
       value: {
         type: Array
       },
       eventNames: {
          type: Array,
          default: function() {
            return [
                {
                    name: 'Before::Create',
                    label: {
                        locales: {
                            en_US: 'Before create',
                            sr: 'Pre kreiranja'
                        }
                    }
                },
                {
                    name: 'Create',
                    label: {
                        locales: {
                            en_US: 'Create',
                            sr: 'Kreirano'
                        }
                    }
                },
                {
                    name: 'Before::Update',
                    label: {
                        locales: {
                            en_US: 'Before update',
                            sr: 'Pre ažuriranja'
                        }
                    }
                },
                {
                    name: 'Update',
                    label: {
                        locales: {
                            en_US: 'Update',
                            sr: 'Ažurirano'
                        }
                    }
                },
                {
                    name: 'Before::Delete',
                    label: {
                        locales: {
                            en_US: 'Before delete',
                            sr: 'Pre brisanja'
                        }
                    }
                },
                {
                    name: 'Delete',
                    label: {
                        locales: {
                            en_US: 'Delete',
                            sr: 'Obrisano'
                        }
                    }
                },
                {
                    name: 'Recycle',
                    label: {
                        locales: {
                            en_US: 'Recycle',
                            sr: 'Recikliraj'
                        }
                    }
                }
            ];
          }
       },
       uuid: {
          type: String
       },

       disableDryRun: {
          type: Boolean,
          default: false
       }
    },
    data: function() {
        return {
            version: 1,
            nodes: [],
            connections: [],
            nodeModal: {
                show: false
            },
            isFullscreen: false,
            newNode: {
                modal: false,
                data: {
                    id: "",
                    name: "",
                    x: 0,
                    y: 0,
                    type: "",
                    action: "",
                    component: "",
                    paused: false,
                    parallel: true,
                    data: {},
                    connections: []
                }
            },
            editNode: {
                modal: false,
                data: {}
            },
            actions: [
                {
                    value: "email",
                    text: this.$t('sendEmail')
                },
                // { we need to support this option better
                //     value: "push_notification",
                //     text: this.$t('pushNotification')
                // },
                {
                    value: "http_request",
                    text: this.$t('httpRequest')
                },
                // {
                //     value: "component",
                //     text: this.$t('component')
                // },
                {
                    value: "expression",
                    text: this.$t('expression')
                },
                {
                    value: "platform_notification",
                    text: this.$t('platformNotification')
                }
            ],
            errors: [],
            requestData: [],
            timers: [
                {
                    value: "30min",
                    text: this.$t('every30')
                },
                {
                    value: "1h",
                    text: this.$t('everyHour')
                },
                {
                    value: "1d",
                    text: this.$t('everyDay')
                }
            ],
            recycleExprErrors: [],
            controlsContent: 'Canvas Double click: Add new node <br />',
            saving: false,

            editConnection: {
                name: ''
            },
            editConnectionModal: false,
            showDryRunResults: true,
            dryRunResults: [],
            resultsSidebar: false,
            inspectResult: null,
            dryRunInput: null,
            dryRunInputModal: false,
            dryRunInputErrors: [],
            dryRunEvent: null,
            dryRunMockedModel: {},
            selectedNode: null,
            globalEnvVars: null
        }
    },
    computed: {
        content: {
            get() {
                return this.value
            },

            set(val) {
                this.$emit('input', val)
            }
        },

        rulesetLibs() {
            return [
                {
                    name: 'Lodash',
                    varName: "_",
                    docs: "https://lodash.com/"
                },
                {
                    name: 'moment',
                    varName: "moment",
                    docs: "https://momentjs.com/"
                }
            ]
        },

        newNodeName: {
            set(val) {
                this.newNode.data.name = val.replaceAll(" ","_");
            },

            get() {
                return this.newNode.data.name.replaceAll(" ", "_");
            }
        },

        editConnectionName: {
            set(val) {
                this.editConnection.name = val.replaceAll(" ","_");
            },

            get() {
                return this.editConnection.name
            }
        },

        editNodeName: {
            set(val) {
                this.editNode.data.name = val.replaceAll(" ","_");
            },

            get() {
                return this.editNode.data.name.replaceAll(" ", "_");
            }
        },

        isDryRunCustomEvent() {
            return this.eventNamesFormated.indexOf(this.dryRunEvent) === -1
        },

        eventNamesFormated() {
            return this.eventNames.map(e => {
                e.label = this.$translateLabel(e.label, e.name)

                return e
            })
        }
    },
    watch: {
        "editNode.data": {
            handler: _.debounce(function() {
                //this.$refs.chart.save()
            }, 500),

            deep: true
        }
    },
    methods: {
        modelChanged(m) {
            this.dryRunMockedModel = m 
        },

        toggleResultsSidebar() {
            this.resultsSidebar = !this.resultsSidebar
        },

        handleChartSave(nodes, connections) {
            nodes.map(n => {
                n.connections = [];

                return n;
            })

            connections.forEach(c => {
                let sourceId = c.source.id

                let node = nodes.find(n => {
                    return n.id == sourceId;
                })

                // check does connection already exists
                let exist = node.connections.find(nodec => {
                    return nodec.source.id == c.source.id && nodec.destination.id == c.destination.id
                })

                if (!exist) {
                    node.connections.push({
                        source: c.source,
                        destination: c.destination,
                        type: c.type,
                        id: c.id,
                        name: c.name
                    })
                }
            })

            this.content = JSON.parse(JSON.stringify(nodes))
            this.$emit('save', this.content)
        },

        handleEditNode(node) {
            // node each can't be edited
            if (node.type == "each") return;

            this.editNode.data = node
            this.editNode.modal = true
        },

        handleEditConnection(conn) {
            let sourceId = conn.source.id
            let sourceNode = this.nodes.find(n => {
                return n.id == sourceId
            })

            if (sourceNode.type == 'switch') {
                //console.log(connection)
                this.editConnection = conn
                this.editConnectionModal = true
            }
        },

        handleDblClick(e) {
           this.newNode.modal = true
           this.newNode.data.x = e.x
           this.newNode.data.y = e.y
        },

        handleRemoveNode(node) {
            this.$refs.chart.remove(node)
            this.$refs.chart.save()
        },  

        drawConnection(conn, internalConnections, internalNodes) {
            conn.name = null

            let sourceId = conn.source.id
            let destinationId = conn.destination.id;
            let sourceNode = internalNodes.find(n => {
                return n.id == sourceId
            })
            let destinationNode = internalNodes.find(n => {
                return n.id == destinationId
            })

            if (sourceNode !== undefined && destinationNode !== undefined) {
                // operation can have only one link
                if (destinationNode.type == "operation") {
                    let sourceConnection = internalConnections.find(c => {
                        return c.destination.id == destinationId
                    })

                    if (sourceConnection !== undefined) {
                        return false;
                    }
                }

                if (sourceNode.type == "condition") {
                    if (conn.source.position == "right") {
                        conn.type = "reject";
                    }
                }

                if (destinationNode.type == "condition") {
                    if (conn.destination.position !== "top") {
                        return false
                    }
                }

                // event can't be connected to event
                if (sourceNode.type == "event" && destinationNode.type == "event") {
                    return false
                }

                // operation can't be connected to event
                if (sourceNode.type == "operation" && destinationNode.type == "event") {
                    return false
                }

                if (sourceNode.type == "condition" && destinationNode.type == "event") {
                    return false
                }

                // circular connections
                if (sourceNode.type == "operation" && destinationNode.type == "operation") {
                    let sourceConnection = internalConnections.find(c => {
                        return c.source.id == sourceNode.id && c.destination.id == sourceNode.id
                    })

                    if (sourceConnection !== undefined) {
                        return false;
                    }
                }
            }

            return true;
        },

        createConnection(conn, internalConnections, internalNodes) {
            let drawn = this.drawConnection(conn, internalConnections, internalNodes)
            if (drawn) {
                internalConnections.push(conn)
                this.handleChartSave(internalNodes, internalConnections)
            }

            return drawn
        },

        dragend: _.debounce(function() {
            this.$refs.chart.save()
        }, 1000),

        newNodeAdd(e) {

            e.preventDefault();

            let node = {
                ...this.newNode.data,
                id: _.random(10000000, 19999999),
            };

            if (node.type != 'each' && (node.name == '' || node.type == '')) {
                return;
            }

            this.$refs.chart.add(node)

            this.newNode.data = {
                name: "",
                type: "",
                x: 0,
                y: 0,
                action: "",
                connections: [],
                data: {}
            }

            this.$refs.chart.save()
        },

        nodeEdit() {
            let editedNode = this.editNode.data

            let nodes = this.nodes.map(n => {
                if (n.id == editedNode.id) {
                    n = editedNode
                }

                return n
            })

            this.$emit("update:nodes", nodes)
        },

        closeNodeEdit(e) {
           if (this.checkForErrors()) {
               e.preventDefault()
               this.$bvModal.msgBoxOk("Some fields are not valid!")
           }
        },

        fieldError(error) {
            let found = this.errors.find((i, e) => {
                return i.field == error.field
            })

            if (found === undefined) {
                this.errors.push(error)
            } else {
                this.errors.map(e => {
                    if (found.field == e.field)
                        e.error = error.error
                })
            }
        },

        checkForErrors() {
            return this.errors.find((i) => {
                return i.error
            });
        },

        recycleExprInput: _.debounce(function(code) {
            if (code === "") return;

            try {
                this.recycleExprErrors = [];
                JSON.parse(code);
            } catch(e) {
                this.recycleExprErrors.push(e)
            }

        }, 1000),

        dryRun(event) {
            this.dryRunInputModal = false

            if (!event) {
                event = this.dryRunEvent
            }

            let data = {
                component_uuid: this.uuid,
                event: event,
                dry_run: true,
                nodes: this.content,
            }

            if (this.dryRunMockedModel) {
                data.model_collection = [this.dryRunMockedModel]
            }

            let popup = this.$loadingPopup("Collecting results...")
            this.$workflowApi.post("/workflow/component/dryrun", data).then(resp => {
                this.showDryRunResults = true
                this.dryRunResults = resp.data
                this.nodes = this.content
                this.version++
            }).catch(err => {
                this.showDryRunResults = true
                this.dryRunResults = err.response.data
                this.nodes = this.content
                this.version++
                
                this.$debugLog(err)
            }).finally(() => {
                popup.hide()
            })
        },

        attemptDryRun(event) {
            if (event == "Recycle") {
               return this.dryRun(event)
            }

            this.dryRunInputModal = true
            this.dryRunEvent = event
        },

        showResultsForNode(results) {
            this.inspectResult = results
            this.resultsSidebar = true
        },

        onDryInput: _.debounce(function(code) {
            if (code === "") return;

            try {
                this.dryRunInputErrors = [];
                JSON.parse(code);
            } catch(e) {
                this.dryRunInputErrors.push(e)
            }

        }, 1000),

        eventNameReducer: ({label, name}) => {
            if (!name) {
                return label
            }

            return name
        },

        render(g, node, isSelected) {
            const nodeColor = "#222528";
            node.width = node.width || 120;
            node.height = node.height || 60;

            let borderColor
            if (node.paused) {
                borderColor = "#FFCC6F"
            } else {
                borderColor = isSelected ? "#666666" : "#6C63FB";
            }

            if (isSelected) {
                this.selectedNode = node
            }

            let bodyTextY = node.y + 5 + node.height / 2;
            
            let hasResults = this.dryRunResults.find(r => {
                return r.id == node.id
            })

            if (!this.disableDryRun) {
                if (hasResults) {
                    if (hasResults.State.Results) {
                        let debug = g.append("text").attr("class", "results")
                            .attr("x",node.x + node.width - 20)
                            .attr("y", bodyTextY - 45)
                            .style("fill", "#4dbd74")
                            .style("font-size", "12px")
                            .style("cursor", "pointer")
                            .html("&#128736;")
                            .style("opacity", "0.4")

                        if (isSelected) {
                            debug.style("opacity", "1")
                        }

                        g.selectAll('.results')
                            .on("click", () => {
                                this.showResultsForNode(hasResults)
                                d3.event.stopPropagation();
                            })
                    }
                }
            }

            if (node.type == "event") {
                let body = g.append("rect")
                .style("width", node.width + "px")
                .style("height", `${node.height}px`)
                .style("word-wrap", "break-word")
                .style("fill", nodeColor)
                .style("stroke-width", "1px");

                body.attr("x", node.x).attr("y", node.y);
                body.attr("stroke", borderColor);
                body.attr("rx", 10);

                const eventName = this.eventNamesFormated.find(e => {
                    return e.name == node.name
                })

                let text = node.name
                if (eventName) {
                    text = eventName.label
                }

                let moveText = 0

                if (!this.disableDryRun) {
                    moveText = 10
                }

                // i want this text to be multiline
                const textElement = g.append("text")
                    .attr("x", node.x + node.width / 2)
                    .attr("y", bodyTextY - moveText)
                    .style("fill", "white")
                    .style("font-size", "12px")
                    .attr("class", "unselectable")
                    .attr("text-anchor", "middle")
                    .text(() => {
                        return text;
                    });

                body.style("width", textElement.node().getBBox().width + 20 + "px")
                node.width = textElement.node().getBBox().width + 20

                if (!this.disableDryRun) {
                    let play = g.append("text").attr("class", "dryRun")
                        play.attr("x",(node.x + node.width / 2) - 5)
                            .attr("y", bodyTextY + 10)
                            .style("fill", "#4dbd74")
                            .style("cursor", "pointer")
                            .html("&#9654;")
                            .style("font-size", "20px")

                        g.selectAll('.dryRun')
                            .on("click", () => {
                                this.attemptDryRun(node.name)
                                d3.event.stopPropagation();
                            })
                    
                    if (!isSelected) {
                        play.style("fill", "#ccc")
                    }
                }
            }

             if (node.type == "each") {
                let body = g.append("ellipse").attr("class", "body");
                body.attr("cx", node.x + node.width / 2);
                body.attr("cy", node.y + node.height / 2);
                body.attr("rx", node.width / 2);
                body.attr("ry", node.height / 2);
                body.style("fill", nodeColor);
                body.style("stroke-width", "1px");
                body.classed(node.type, true);
                body.attr("stroke", borderColor);


                g.append("foreignObject")
                .attr("x", node.x + node.width / 2 - 10) // Adjust position as needed
                .attr("y", node.y + node.height / 2 - 15) // Adjust position as needed
                .attr("width", 20) // Adjust size as needed
                .attr("height", 25) // Adjust size as needed
                .html('<i class="bi bi-repeat" style="font-size: 20px; color: white;"></i>');
            }

            if (node.type == "operation") {
                let heading = g.append("rect")
                    .attr("x", node.x)
                    .attr("y", node.y)
                    .attr("stroke", borderColor)
                    .attr("class", "title")
                    .style("height", "20px")
                    .style("fill", nodeColor)
                    .style("stroke-width", "0.5px")
                    .style("width", node.width + "px");

                const headingText = g.append("text")
                    .attr("x", node.x + 4)
                    .attr("y", node.y + 15)
                    .style("fill", "white")
                    .attr("class", "unselectable")
                    .text(() => node.name)

                let body = g.append("rect").attr("class", "body")
                .style("width", node.width + "px")
                .style("stroke-width", "1px");

                body.attr("x", node.x).attr("y", node.y + 20);
                body.style("height", node.height - 20 + "px");
                body.attr("stroke", borderColor);

                let bodyText = g.append("text")
                    .attr("dy", node.y + (node.height - 20) / 2 + 25)
                    .attr("width", node.width)
                    .attr("x", node.x + node.width / 2)
                    .style("fill", "#ccc")
                    .attr("text-anchor", "middle")
                    .text(() => node.action)
  

                const bodyTextElement = bodyText.node().getBBox().width
                const headingTextElement = headingText.node().getBBox().width

                const size = bodyTextElement > headingTextElement ? bodyTextElement : headingTextElement

                body.style("width", size + 20 + "px")
                heading.style("width", size + 20 + "px")
                node.width = size + 20
            }

            if (node.type == "switch") {
                let defs = g.append('defs')
                let path = defs.append('path')
                path.attr("id", "Switch")
                path.attr("d", "M 60 0 120 30 60 60 0 30 Z")
                path.attr("fill", nodeColor)
                path.attr("stroke", borderColor)
                path.attr("stroke-width", "1px")

                let use = g.append("use")
                use.attr("x", node.x)
                use.attr("y", node.y)
                use.attr("xlink:href", "#Switch")

                const textElement = g.append("text")
                    .attr("y", node.y + 35)
                    .style("fill", "white")
                    .attr("class", "unselectable")
                    .attr("text-anchor", "middle")
                    .text(() => node.name)

                
                const textWidth = textElement.node().getBBox().width;
                const newWidth = textWidth + 40; // Add some padding

                // Adjust the path dimensions based on the new width
                path.attr("d", `M ${newWidth / 2} 0 L ${newWidth} 30 L ${newWidth / 2} 60 L 0 30 Z`);

                // Adjust the use element dimensions
                use.attr("width", newWidth)
                    .attr("height", 60);

                node.width = newWidth;

                // Adjust the text position based on the new width
                textElement.attr("x", node.x + newWidth / 2);

            }

            if (node.type == "condition") {
                let body = g.append("rect")
                    .attr("x", node.x)
                    .attr("y", node.y)
                    .attr("stroke", borderColor)
                    .attr("class", "title")
                    .style("height", "20px")
                    .style("fill", nodeColor)
                    .style("stroke-width", "1px")
                    .style("width", node.width + "px");

                let conditionTrue = g.append("rect").attr("class", "body");
                conditionTrue
                .style("fill", "#4DBD74")
                .style("stroke-width", "1px");

                conditionTrue.attr("x", node.x).attr("y", node.y + 20);
                conditionTrue.style("height", node.height - 20 + "px");
                conditionTrue.attr("stroke", borderColor);

                let conditionFalse = g.append("rect").attr("class", "body");
                conditionFalse
                .style("fill", "#B55C5D")
                .style("stroke-width", "1px");

                conditionFalse.attr("x", node.x + (node.width / 2)).attr("y", node.y + 20);
                conditionFalse.style("height", node.height - 20 + "px");
                conditionFalse.attr("stroke", borderColor);

                let conditionTrueText = g.append("text")
                    .attr("x", node.x + 15)
                    .attr("y", node.y + 45)
                    .style("fill", "white")
                    .attr("class", "unselectable")
                    .attr("text-anchor", "middle")
                    .text(() => "true");

                let conditionFalseText = g.append("text")
                    .attr("x", node.x + 75)
                    .attr("y", node.y + 45)
                    .attr("class", "unselectable")
                    .style("fill", "white")
                    .attr("text-anchor", "middle")
                    .text(() => "false");


                const textElement = g.append("text")
                    .attr("x", node.x + 4)
                    .attr("y", node.y + 15)
                    .attr("class", "unselectable")
                    .style("fill", "white")
                    .text(() => node.name)

                const textWidth = textElement.node().getBBox().width;
                const newWidth = textWidth + 10; // Add some padding

                body.style("width", newWidth + "px");
                conditionFalse.style("width", newWidth / 2 + "px");
                conditionFalse.attr("x", node.x + newWidth / 2);
                conditionFalseText.attr("x", node.x + newWidth / 2 + 60);

                conditionTrue.style("width", newWidth / 2 + "px");
                conditionTrueText.attr("x", node.x + newWidth / 4);

                node.width = newWidth
            }

            this.content = this.content.map(n => {
                if (n.id == node.id) {
                    return node
                }

                return n
            })
        }
    },

    mounted() {
        this.nodes = this.content || []

        let connections = [];
        this.nodes.forEach(n => {
            let c = JSON.parse(JSON.stringify(n))
            if (c.connections) {
                connections = connections.concat(c.connections)
            }
        })

        this.connections = connections;

        if (this.$store.getters.isLiveEnv) {
            document.onkeyup = function(e) {
                console.log(e)
                if (e.code == 'Delete') {
                    e.preventDefault();
                    e.stopImmediatePropagation()
                }
            }
        }
    }
}
</script>
